import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './DeleteConfirmModal.css';

interface DeleteConfirmModalProps {
  isOpen: boolean;
  title?: string;
  message: string;
  itemName?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  isOpen,
  title,
  message,
  itemName,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation('common');
  const [inputValue, setInputValue] = useState('');
  const [isValid, setIsValid] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setInputValue('');
      setIsValid(false);
      // Focus input after modal opens
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    setIsValid(inputValue.toLowerCase() === 'delete');
  }, [inputValue]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isValid) {
      onConfirm();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="delete-confirm-overlay" onClick={onCancel}>
      <div
        className="delete-confirm-modal"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="delete-confirm-header">
          <h3 className="delete-confirm-title">{title || t('deleteConfirm.defaultTitle')}</h3>
        </div>

        <div className="delete-confirm-body">
          <p className="delete-confirm-message">{message}</p>
          {itemName && (
            <div className="delete-confirm-item">
              <span className="delete-confirm-item-label">{t('deleteConfirm.willDelete')}</span>
              <span className="delete-confirm-item-name">{itemName}</span>
            </div>
          )}

          <div className="delete-confirm-input-section">
            <label
              className="delete-confirm-label"
              dangerouslySetInnerHTML={{ __html: t('deleteConfirm.typeToConfirm') }}
            />
            <input
              ref={inputRef}
              type="text"
              className="delete-confirm-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={t('deleteConfirm.placeholder')}
              autoComplete="off"
            />
          </div>
        </div>

        <div className="delete-confirm-footer">
          <button
            className="delete-confirm-btn delete-confirm-btn--cancel"
            onClick={onCancel}
          >
            {t('deleteConfirm.cancelBtn')}
          </button>
          <button
            className="delete-confirm-btn delete-confirm-btn--delete"
            onClick={onConfirm}
            disabled={!isValid}
          >
            {t('deleteConfirm.deleteBtn')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmModal;
